import classNames from "classnames";
import { useRef, useState } from "react";
import useListFilters from "../../hooks/useListFilters";
import { useTranslations } from "../../hooks/useTranslations";
import {
  SP,
  useAdminMemberSettingsWorksheetsYears,
  useAdminMembersWorksheets,
} from "../../http/serviceportalApi";
import { formatDateTime, getRemoteDataStatus } from "../../utils";
import ExpandableRow from "../ui/ExpandableRow";
import Page from "../ui/Page";
import Select from "../ui/Select";
import Table from "../ui/Table";
import styles from "./AdminAccounting.module.css";

const AdminAccounting = () => {
  const { i18n } = useTranslations();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1);

  const { data: yearsData, error: yearsError } =
    useAdminMemberSettingsWorksheetsYears();
  const years = yearsData?.years ?? [];

  const filters = useListFilters({
    isSearchable: true,
  });
  const aborter = useRef(new AbortController());
  const {
    data: worksheetsData,
    isValidating: isValidatingWorksheets,
    error: worksheetsError,
  } = useAdminMembersWorksheets(
    { ...filters.state, year },
    aborter.current.signal,
  );
  const worksheets = worksheetsData?.items ?? [];

  const error = yearsError ?? worksheetsError;

  return (
    <Page title={i18n.admin.accounting.title}>
      <div className={styles.yearSelectWrapper}>
        <label>{i18n.admin.accounting.label.year}</label>
        <Select
          value={year}
          onChange={(ev) => setYear(parseInt(ev.target.value))}
        >
          {years.map((year) => (
            <option key={year}>{year}</option>
          ))}
        </Select>
      </div>
      <Table
        filters={filters}
        searchOptions={{
          placeholder: i18n.admin.accounting.label.searchMemberPlaceholder,
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        hasNextPage={worksheetsData?.next_page !== undefined}
        isTall={true}
        remoteDataStatus={getRemoteDataStatus({
          isValidating: isValidatingWorksheets,
          error,
        })}
        head={
          <ExpandableRow
            CellType="th"
            cells={[
              <th align="left" key="name">
                {i18n.admin.accounting.label.member}
              </th>,
              <th align="left" key="status">
                {i18n.admin.accounting.label.status}
              </th>,
              <th align="left" key="end_date">
                {i18n.admin.accounting.label.endDate}
              </th>,
            ]}
          />
        }
        body={worksheets.map((member) => (
          <ExpandableRow
            key={member.id}
            CellType="td"
            cells={[
              <td key="name">{member.name}</td>,
              <td key="status">
                <StatusLight status={member.status} />
              </td>,
              <td key="end_date">
                {formatDateTime(new Date(member.end_date))}
              </td>,
            ]}
            expandedContent={
              <Table
                head={
                  <tr>
                    <th align="left" className={styles.company}>
                      {i18n.admin.accounting.label.company}
                    </th>
                    <th align="left">{i18n.admin.accounting.label.status}</th>
                  </tr>
                }
                body={member.companies.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>
                      <StatusLight status={c.status} />
                    </td>
                  </tr>
                ))}
              />
            }
          />
        ))}
      />
    </Page>
  );
};

interface StatusLightProps {
  status: SP.Accounting.Admin.WorksheetStatus;
}

const StatusLight = ({ status }: StatusLightProps) => {
  const { i18n } = useTranslations();
  return (
    <div className={styles.statusLightWrapper}>
      <div
        className={classNames(styles.statusLight, styles[status])}
        title={status}
      />
      {i18n.admin.accounting.worksheet.status[status]}
    </div>
  );
};

export default AdminAccounting;
